import React from 'react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Card, CardContent } from '../components/ui/card';
import { Globe, Coins, Lock, Users, Wallet, Code } from 'lucide-react';

export const Web3 = () => {
  const features = [
    {
      icon: Lock,
      title: 'Decentralized Finance (DeFi)',
      description: 'Access financial services without intermediaries through smart contracts and blockchain technology.',
    },
    {
      icon: Wallet,
      title: 'Digital Ownership',
      description: 'True ownership of digital assets through NFTs and tokenization on blockchain networks.',
    },
    {
      icon: Code,
      title: 'Smart Contracts',
      description: 'Self-executing contracts with automated enforcement and transparent terms.',
    },
    {
      icon: Users,
      title: 'Community Governance',
      description: 'Participate in decentralized autonomous organizations (DAOs) with voting rights.',
    },
    {
      icon: Coins,
      title: 'Cryptocurrency Integration',
      description: 'Seamless integration with major cryptocurrencies and digital tokens.',
    },
    {
      icon: Globe,
      title: 'Global Accessibility',
      description: 'Borderless financial services accessible to anyone with internet connection.',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f] overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1639762681485-074b7f938ba0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxibG9ja2NoYWluJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzM3MzU2NzZ8MA&ixlib=rb-4.1.0&q=85')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Web3 Technology
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            The decentralized internet powered by blockchain and digital ownership
          </p>
        </div>
      </section>

      {/* Main Content */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
              <div>
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                  Understanding Web3
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Web3 represents the next evolution of the internet, built on decentralized technologies including blockchain, digital ownership, and decentralized finance (DeFi).
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Unlike Web2, where large corporations control user data and platforms, Web3 empowers individuals with true ownership of their digital assets, data, and online identities. This paradigm shift enables peer-to-peer transactions without intermediaries.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Through smart contracts and distributed ledger technology, Web3 creates trustless systems where code, not institutions, enforces agreements and facilitates transactions.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1631864031824-d636e1dc5292?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxibG9ja2NoYWluJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzM3MzU2NzZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Web3 Blockchain Technology"
                  className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#FFD700] rounded-full filter blur-3xl opacity-20" />
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Features Grid */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
                Web3 Capabilities
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Explore the powerful features of decentralized technology
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="group h-full bg-white border-2 border-transparent hover:border-[#FFD700] shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <feature.icon className="w-8 h-8 text-[#FFD700]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0B2A4A] mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Use Cases Section */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 relative">
                <img
                  src="https://images.unsplash.com/photo-1707075891514-6b72a31a35cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxmaW50ZWNoJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzM3MzU2NzB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Cryptocurrency and Web3"
                  className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -top-6 -left-6 w-48 h-48 bg-[#0B2A4A] rounded-full filter blur-3xl opacity-10" />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                  Real-World Applications
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Web3 technology is transforming multiple industries:
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Financial Services:</strong> Lending, borrowing, and trading without traditional banks
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Digital Art & Media:</strong> Creators monetize work through NFTs and direct fan engagement
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Gaming:</strong> True ownership of in-game assets and play-to-earn models
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Identity Management:</strong> Self-sovereign digital identities controlled by users
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Supply Chain:</strong> Transparent tracking and verification of products
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Vision Section */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
              The Web3 Vision
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Web3 envisions an internet where users have control over their data, digital assets, and online experiences. By removing centralized intermediaries, Web3 creates more equitable digital economies where value flows directly to creators and participants.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              As blockchain technology matures and adoption grows, Web3 has the potential to fundamentally reshape how we interact, transact, and build communities online.
            </p>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default Web3;
